import * as React from 'react';
import {FC, useEffect, useState} from 'react';
import {Loader} from '@flexe/ui-components';
import EdiFilesService from '../edi-files/EdiFilesService';
import {DisplayGenericErrors, GenericErrorDisplay} from '../../shared/GenericErrorDisplay';
import FileStorageService from '../file-storage/FileStorageService';
import EdiFilesServiceV2 from '../edi-files/EdiFilesServiceV2';
import {FilterValue} from '../../shared/CommonInterfaces';
import OutboundOrders, {Props, ServiceTypes} from './OutboundOrders';
import OutboundOrdersService from './OutboundOrdersService';
import CreateSingleOrderButton from './buttons/CreateSingleOrderButton';
import BulkCancelOrdersModalButton from './buttons/BulkCancelOrdersModalButton';
import OutboundOrdersTable from './OutboundOrdersTable';
import BulkCreateOrdersModalButton from './buttons/BulkCreateOrdersModalButton';
import {OrdersViewType} from './OutboundOrdersInterfaces';
import OrdersViewTypeButton from './buttons/OrdersViewTypeButton';
import PageSizeInput, {PageSize} from './generic/PageSizeInput';
import OutboundOrdersUploadModal from './OutboundOrdersUploadModal';
import {
  csvCreateFileUploadTypeV1,
  csvCreateFileUploadTypeV2,
  csvCreateHeadersV1,
  csvCreateHeadersV2,
  csvCreateOrderByLotHeaders
} from './helpers/OutboundOrderCsv';
import OutboundOrdersBulkCancelModal from './OutboundOrdersBulkCancelModal';
import OutboundOrderLinesTable from './OutboundOrderLinesTable';
import {buildReservationOptions} from './helpers/Reservations';
import {
  blankFilters,
  extractFiltersFromSearchParams,
  extractNewFilters,
  FilterDefinition,
  filterKeyCreatedStarting,
  getDefaultCreatedAtFilters,
  orderFilterDefs,
  orderLineFilterDefs,
  TrackedFilterValues
} from './helpers/OutboundOrdersFilters';
import {getURLSearchParams} from './helpers/URLManipulation';

/***** TODO before release
 * Update URLs when filters are changed
 * Cancel lines button
 */

export interface OrdersSearchPageState {
  pageSize: PageSize;
  viewType: OrdersViewType;
  filterValues: TrackedFilterValues; // There's a few ways to solve it, but tracking filters here because we can grab them from the URL
  showBulkCancellationModal: boolean;
  showBulkCreateModal: boolean;
  errors: GenericErrorDisplay[];
}

const initialOrdersSearchPageState: OrdersSearchPageState = {
  pageSize: PageSize.Fifty,
  viewType: OrdersViewType.orders,
  filterValues: blankFilters,
  showBulkCancellationModal: false,
  showBulkCreateModal: false,
  errors: []
};

export interface OrdersPageError {
  errorType: 'None' | 'UnexpectedFailure' | 'ApiResponse';
  details?: string[];
}

interface CustomerConfiguration {
  carriers: string[];
  serviceTypes: ServiceTypes;
}
const initialCustomerConfiguration = null;
const bulkOrderCreateTemplatePath = '/static/files/OutboundOrderBulkCreateV2.csv';
const orderByLotCreateTemplatePath = '/static/files/OutboundOrderByLotBulkCreate.csv';

export const dateFormat = 'MM/DD/YY h:mma';

const loadCustomerConfiguration = async (ediFilesService: EdiFilesService): Promise<CustomerConfiguration> => {
  if (!ediFilesService) {
    return initialCustomerConfiguration;
  }

  const data = await ediFilesService.getCarriersAndServiceTypes();
  // this ridiculousness is to remove a ® character from the
  // API-returned value for the carrier UPS
  const cleanedServiceTypes = data.data.serviceTypes;
  /* eslint-disable */
  cleanedServiceTypes['UPS'] = cleanedServiceTypes['UPS®'];
  delete cleanedServiceTypes['UPS®'];
  /* eslint-enable */

  return {
    carriers: data.data.carriers,
    serviceTypes: cleanedServiceTypes
  };
};

const getInitialViewType = (): OrdersViewType => {
  const urlSearchParams = getURLSearchParams();
  const inputViewType = urlSearchParams.get('viewType');
  const matchingViewType = Object.values(OrdersViewType).find((e) => e.valueOf() === inputViewType);

  return matchingViewType ? matchingViewType : OrdersViewType.orders;
};

const getFilterDefs = (viewType: OrdersViewType): FilterDefinition[] =>
  viewType === OrdersViewType.orders ? orderFilterDefs : orderLineFilterDefs;

const buildInitialFilters = (viewType: OrdersViewType): TrackedFilterValues => {
  const urlSearchParams = getURLSearchParams();
  const initialFilters = extractFiltersFromSearchParams(urlSearchParams, getFilterDefs(viewType));

  if (Object.keys(initialFilters.inputFilters).length < 1) {
    const [displayValue, apiParam] = getDefaultCreatedAtFilters();
    initialFilters.inputFilters[filterKeyCreatedStarting] = displayValue;
    initialFilters.apiFilters[filterKeyCreatedStarting] = apiParam;
  }

  return initialFilters;
};

const OutboundOrdersV2: FC<Props> = (props) => {
  const [ordersSearchPageState, setOrdersSearchPageState] = useState<OrdersSearchPageState>(
    initialOrdersSearchPageState
  );
  const [customerConfiguration, setCustomerConfiguration] = useState<CustomerConfiguration>(
    initialCustomerConfiguration
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const viewType = getInitialViewType();
    const filterValues = buildInitialFilters(viewType);

    setOrdersSearchPageState({...ordersSearchPageState, viewType, filterValues});
    loadCustomerConfiguration(props.ediFilesService || new EdiFilesService(props.authenticityToken)).then((result) => {
      setCustomerConfiguration(result);
      setIsLoading(false);
    });
  }, []);

  const toggleBulkCreateModal = () =>
    setOrdersSearchPageState({
      ...ordersSearchPageState,
      showBulkCreateModal: !ordersSearchPageState.showBulkCreateModal
    });
  const toggleBulkCancellationModal = () =>
    setOrdersSearchPageState({
      ...ordersSearchPageState,
      showBulkCancellationModal: !ordersSearchPageState.showBulkCancellationModal
    });
  const setViewType = (newViewType: OrdersViewType) => {
    if (newViewType !== ordersSearchPageState.viewType) {
      setOrdersSearchPageState({...ordersSearchPageState, viewType: newViewType});
    }
  };
  const handleFilterChange = (filters: FilterValue[]): void => {
    const filterValues = extractNewFilters(filters);
    setOrdersSearchPageState({...ordersSearchPageState, filterValues});
  };

  const setErrors = (rawError: OrdersPageError) => {
    const errors: GenericErrorDisplay[] = [];
    switch (rawError.errorType) {
      case 'UnexpectedFailure':
        errors.push({
          header: 'Unexpected error. Please check your connection and refresh. Contact Flexe if the error persists.',
          details: rawError.details
        });
        break;

      case 'ApiResponse':
        errors.push({
          header: 'Failed to retrieve data:',
          details: rawError.details
        });
    }

    setOrdersSearchPageState({...ordersSearchPageState, errors});
  };
  const setPageSize = (v: PageSize) => setOrdersSearchPageState({...ordersSearchPageState, pageSize: v});

  // TODO remove all showOldView stuff prior to customer release
  const [showOldView, setShowOldView] = useState<boolean>(false);
  if (showOldView) {
    return <OutboundOrders {...props} />;
  }

  if (isLoading) {
    return (
      <div className="container-fluid fulfillment-index outbound-orders">
        <Loader loading={isLoading} />
      </div>
    );
  }

  const reservationOptions = buildReservationOptions(props.omsReservations);
  const outboundOrdersService = props.outboundOrdersService || new OutboundOrdersService();
  const fileStorageService = props.fileStorageService || new FileStorageService();
  const ediFilesServiceV2 = props.v2EdiFilesService || new EdiFilesServiceV2();

  const tableToShow =
    ordersSearchPageState.viewType === OrdersViewType.orders ? (
      <OutboundOrdersTable
        companyId={props.currentCompany.id}
        reservations={props.omsReservations}
        reservationOptions={reservationOptions}
        outboundOrdersService={outboundOrdersService}
        pageSize={ordersSearchPageState.pageSize}
        filterValues={ordersSearchPageState.filterValues}
        handleFilterChange={handleFilterChange}
        setErrors={setErrors}
      />
    ) : (
      <OutboundOrderLinesTable
        outboundOrdersService={outboundOrdersService}
        pageSize={ordersSearchPageState.pageSize}
        filterValues={ordersSearchPageState.filterValues}
        handleFilterChange={handleFilterChange}
        setErrors={setErrors}
        authenticityToken={props.authenticityToken}
        currentCompany={props.currentCompany}
        reservations={props.omsReservations}
        reservationOptions={reservationOptions}
        distributionByLotReservationIds={props.distributionByLotReservationIds}
        showCalculatedShipByDate={props.featureFlags.showCalculatedShipByDate}
      />
    );

  const distributionByLotShipper = props.distributionByLotReservationIds.length > 0;
  const templatePath = distributionByLotShipper ? orderByLotCreateTemplatePath : bulkOrderCreateTemplatePath;
  const csvHeaders = distributionByLotShipper ? csvCreateOrderByLotHeaders : csvCreateHeadersV2;

  return (
    <div className="container-fluid fulfillment-index outbound-orders">
      <div className="row">
        <div className="col-sm-4">
          <h1>Outbound Orders</h1>
        </div>
        <div className="col-sm-8">
          <BulkCreateOrdersModalButton toggleModal={toggleBulkCreateModal} />
          <a href="/s/fulfillment/ecommerce" className="btn pull-right">
            View Shipments
          </a>
          <CreateSingleOrderButton
            createNewOrdersButtonFlagOn={props.featureFlags.createNewOrdersButtonFlagOn}
            companyId={props.currentCompany.id}
            outboundOrdersService={outboundOrdersService}
            showOmniOrderCreateBtn={props.shipperHasOmniReservation}
          />
          <BulkCancelOrdersModalButton
            outboundOrdersService={props.outboundOrdersService}
            showOrderCancellationByCsvButton={ordersSearchPageState.showBulkCancellationModal}
            toggleModal={toggleBulkCancellationModal}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <button className="btn" onClick={() => setShowOldView(true)}>
            &nbsp; Show Old Page
          </button>
          <br />
          <br />
        </div>
      </div>
      <div id="fulfillment-component">
        <div className="row">
          <div className="col-sm-11">
            <OrdersViewTypeButton viewType={ordersSearchPageState.viewType} setViewType={setViewType} />
          </div>
          <div className="col-1 pull-right">
            <PageSizeInput
              label="Results per Page"
              value={ordersSearchPageState.pageSize}
              onValueChange={setPageSize}
            />
          </div>
        </div>
        {DisplayGenericErrors(ordersSearchPageState.errors)}
        {tableToShow}
      </div>
      <OutboundOrdersUploadModal
        templatePath={templatePath}
        distributionByLotShipper={distributionByLotShipper}
        serviceTypes={customerConfiguration.serviceTypes}
        carriers={customerConfiguration.carriers}
        showUploadModal={ordersSearchPageState.showBulkCreateModal}
        headers={csvHeaders}
        fileUploadType={csvCreateFileUploadTypeV2}
        fileStorageService={fileStorageService}
        ediFilesServiceV2={ediFilesServiceV2}
        // TODO: delete oldRequiredHeaders once old template has been retired
        oldHeaders={csvCreateHeadersV1}
        oldFileUploadType={csvCreateFileUploadTypeV1}
        toggleUploadModal={toggleBulkCreateModal}
      />
      <OutboundOrdersBulkCancelModal
        currentCompany={props.currentCompany}
        showUploadModal={ordersSearchPageState.showBulkCancellationModal}
        toggleUploadModal={toggleBulkCancellationModal}
        authenticityToken={props.authenticityToken}
      />
    </div>
  );
};
export default OutboundOrdersV2;
